import { SdkContext } from '../internal'
import { queuedExportOf, type QueuedExport } from '../fileDownload'
import type { EngagementActivity, EngagementActivityFilters, EngagementActivityPage, EngagementReport } from '@/types/engagement'

/** Only the chosen filters; `kind` goes comma-separated, which the API accepts. */
const activityQuery = (f: EngagementActivityFilters & { cursor?: string; limit?: number }): Record<string, unknown> =>
  Object.fromEntries(
    Object.entries({ ...f, kind: f.kind && f.kind.length > 0 ? f.kind.join(',') : undefined }).filter(
      ([, v]) => v !== undefined && v !== null && v !== ''
    )
  )

/** F-87: the engagement page. The whole team reads it (owner, admin, moderator, finance). */
export class EngagementResource {
  constructor(private ctx: SdkContext) {}

  /** Daily series, totals, `compare` with the previous window of the same length, and the most engaged. */
  async report(params: { from: string; to: string; interval?: 'day' | 'week' | 'month'; limit?: number }): Promise<EngagementReport> {
    /* The report carries `pagination` (for `members`) beside an object `data`; read the envelope whole. */
    const body = await this.ctx.envelope<{ data: EngagementReport }>(
      { httpMethod: 'GET', path: '/reports/engagement' },
      { query: { interval: 'day', ...params } }
    )
    return body.data
  }

  /**
   * The statement, newest first, by cursor. The envelope is read whole because `pagination`
   * (hasMore, nextCursor) sits beside `data`, which the shared client drops.
   */
  async activities(filters: EngagementActivityFilters & { cursor?: string; limit?: number }): Promise<EngagementActivityPage> {
    const body = await this.ctx.envelope<{
      data?: { items?: EngagementActivity[] }
      pagination?: { hasMore?: boolean; nextCursor?: string | null }
    }>({ httpMethod: 'GET', path: '/reports/engagement/activities' }, { query: activityQuery(filters) })
    return {
      items: body?.data?.items || [],
      hasMore: Boolean(body?.pagination?.hasMore),
      nextCursor: body?.pagination?.nextCursor || null
    }
  }

  /**
   * The same statement as CSV, columns in the reader's language; 422 above 50,000 rows.
   * Above 2,000 rows the file is e-mailed (202, `QueuedExport`).
   */
  async activitiesCsv(filters: EngagementActivityFilters): Promise<string | QueuedExport> {
    const result = await this.ctx.raw<unknown>(
      { httpMethod: 'GET', path: '/reports/engagement/activities' },
      { query: { ...activityQuery(filters), format: 'csv' } }
    )
    if (typeof result === 'string') return result
    return queuedExportOf(result) || String(result ?? '')
  }
}
